import CameraControls from "camera-controls";
import * as THREE from "three";
import { EventEmitter } from "events";
import { CameraPosition } from "../visuals/visual-structs";
import { copyVec3 } from "../visuals/visual-utils";
import { MREvents } from "./MREvents";
import { BaseMRService } from "./modelpanelparts/BaseMRService";
import { ModelDataService } from "./modelpanelparts/ModelDataService";
import { ModelLoader } from "./common/ModelLoader";
import { MRUserEventer } from "./modelpanelparts/MRUserEventer";
import { BaseAnimator, DefaultAnimator } from "../visuals/BaseAnimator";

export class ModelRenderer extends EventEmitter {
  public camera: THREE.PerspectiveCamera;
  public cameraControls!: CameraControls;
  public renderer: THREE.WebGLRenderer;
  public scene: THREE.Scene = new THREE.Scene();
  public sceneWidth: number;
  public sceneHeight: number;
  private clock = new THREE.Clock();
  private targetFPS = 60;
  private frameInterval = 1000 / this.targetFPS;
  public services: BaseMRService[] = [];
  public dataService: ModelDataService;
  public loader = new ModelLoader();
  public animator: BaseAnimator = new DefaultAnimator();
  constructor(private div: HTMLDivElement) {
    super();
    // Clean container
    while (div.firstChild) div.removeChild(div.firstChild);
    this.sceneWidth = div.clientWidth;
    this.sceneHeight = div.clientHeight;
    // Camera setup
    this.camera = new THREE.PerspectiveCamera(
      75,
      this.sceneWidth / this.sceneHeight,
      0.1,
      2000000000
    );
    // this.camera.name = "FPVCAMERA";
    this.camera.position.set(0, 0, 10); // Move the camera higher    
    this.camera.up.set(0, 0, 1);
    this.camera.lookAt(0, 0, 0); // Focus on the center
    this.camera.updateProjectionMatrix(); // Update the camera matrix after changes

    this.renderer = new THREE.WebGLRenderer({
      antialias: true, // Retained for smooth edges
      alpha: true, // Retained for transparent background if needed
      powerPreference: "high-performance", // Retained for optimal GPU usage
      precision: "highp", // Use highest precision for shaders
      logarithmicDepthBuffer: true, // Improve depth precision for large scenes
    });

    // this.renderer.physicallyCorrectLights = true; // Enable physically correct lighting
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace; // Accurate color rendering
    // Set pixel ratio to match device for crisp visuals
    this.renderer.setPixelRatio(window.devicePixelRatio);

    // Enable shadow mapping with high-quality settings
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Soft shadows for smoother edges

    // Optional: Tone mapping for better dynamic range
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0; // Adjust exposure as needed
    // this.renderer.setSize(this.sceneWidth, this.sceneHeight);
    this.setSize(this.sceneWidth, this.sceneHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    div.appendChild(this.renderer.domElement);
    this.initCameraControls();
    // this.renderTestBox();
    
    const axesHelper = new THREE.AxesHelper(50);
    this.scene.add(axesHelper);
    const gridHelper = new THREE.GridHelper(100, 100);

    gridHelper.rotation.x = Math.PI / 2; // Rotate grid to be horizontal
    gridHelper.position.z = -0.01; // Slightly below the origin to avoid z-fighting
    this.scene.add(gridHelper);
    const floorBox = new THREE.Mesh(
      new THREE.BoxGeometry(20, 20, 0.01),
      new THREE.MeshBasicMaterial({ color: 0x006f00, side: THREE.DoubleSide })
    );
    floorBox.position.z = -0.06; // Slightly below the origin to avoid z-fighting
    this.scene.add(floorBox);

    this.scene.add(this.loader.getContainer());
    // add directional light
    const dlRight = new THREE.DirectionalLight(0xffffff, 5);
    dlRight.target.position.set(0, 0, 0); // Point light towards the center
    dlRight.position.set(0, -50, 50);
    this.scene.add(dlRight);
    const dlLeft = new THREE.DirectionalLight(0xffffff, 5);
    dlLeft.target.position.set(0, 0, 0); // Point light towards the center
    dlLeft.position.set(0, 50, 50);
    this.scene.add(dlLeft);

    const ambientLight = new THREE.AmbientLight(0x00ff00, 0.5);
    this.scene.add(ambientLight); 

    this.dataService = new ModelDataService(this);
    this.services = [
      this.dataService,
      new MRUserEventer(this),
    ];
    const loader = async () => {
      for (const service of this.services) {
        await service.load();
      }
      this.clock.start();
      this.startRenderingLoop();
    };

    loader().then(() => {
      // console.log("WorldRenderer initialized and rendering loop started.");
    }).catch((error) => {
      console.error("Error initializing WorldRenderer:", error);
    });
    // this.renderTestBox();
  }

  public initCameraControls() {
    this.cameraControls = new CameraControls(
      this.camera,
      this.renderer.domElement
    );
    // this.cameraControls.maxPolarAngle = Math.PI; // so you can't look under the floor plane
    this.cameraControls.minPolarAngle = 0;
    this.cameraControls.minDistance = 1;
    this.cameraControls.maxDistance = 20000000;
    this.cameraControls.dollySpeed = 0.25;
    this.cameraControls.truckSpeed = 1.5;
    this.cameraControls.azimuthRotateSpeed = 0.15; // Adjust horizontal rotation sensitivity
    this.cameraControls.polarRotateSpeed = 0.15; // Adjust vertical rotation sensitivity
    this.cameraControls.dollyToCursor = true;
    this.cameraControls.smoothTime = 2.5;
  }

  private renderTestBox() {
    const light = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(light);
    const geometry = new THREE.BoxGeometry(1.5, 1, 1);
    const material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    const cube = new THREE.Mesh(geometry, material);
    cube.position.set(0, 0, 0);
    this.scene.add(cube);
  }

  public setCameraPosition(cp: CameraPosition) {
    this.cameraControls.setPosition(cp.position.x, cp.position.y, cp.position.z);
    this.cameraControls.setTarget(cp.target.x, cp.target.y, cp.target.z);
    this.camera.up.set(cp.up.x, cp.up.y, cp.up.z);
    this.cameraControls.updateCameraUp();
    this.camera.updateProjectionMatrix();
  }

  public getCameraPosition(): CameraPosition {
    const lookAt = new THREE.Vector3();
    this.cameraControls.getTarget(lookAt);
    const up = new THREE.Vector3();
    copyVec3(this.camera.up, up);
    const cp: CameraPosition = {
      position: { x: this.camera.position.x, y: this.camera.position.y, z: this.camera.position.z },
      target: {
        x: lookAt.x,
        y: lookAt.y,
        z: lookAt.z
      },
      up
    };
    return cp;
  }

  public setSize(width: number, height: number) {
    this.sceneWidth = width;
    this.sceneHeight = height;
    this.camera.aspect = this.sceneWidth / this.sceneHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.sceneWidth, this.sceneHeight);
  }

  private startRenderingLoop() {
    let lastFrameTime = performance.now();

    const animate = (currentTime: number) => {
      if (currentTime - lastFrameTime < this.frameInterval) {
        requestAnimationFrame(animate);
        return;
      }

      lastFrameTime = currentTime;

      // Handle resizing for main camera
      // if (this.sceneWidth !== this.div.clientWidth || this.sceneHeight !== this.div.clientHeight) {
      //   this.sceneWidth = this.div.clientWidth;
      //   this.sceneHeight = this.div.clientHeight;
      //   this.camera.aspect = this.sceneWidth / this.sceneHeight;
      //   this.camera.updateProjectionMatrix();
      //   this.renderer.setSize(this.sceneWidth, this.sceneHeight);
      // }
      // Render
      const delta = this.clock.getDelta();
      this.cameraControls.update(delta);
      this.animator.render(delta);
      this.renderer.render(this.scene, this.camera);

      // Render each service
      for (const service of this.services) {
        service.render(delta);
      }
      requestAnimationFrame(animate);
    };
    animate(performance.now());
  }

  public dispose() {
    this.renderer.dispose();
    this.div.removeChild(this.renderer.domElement);
    this.cameraControls.dispose();
  }

  public emitEvent(event: MREvents, ...args: any[]) {
    this.emit(event, ...args);
  }

  public onEvent(event: MREvents, listener: (...args: any[]) => void) {
    this.addListener(event, listener);
  }

}